import React, { createContext, useContext, useEffect, useMemo } from 'react'
import { produce } from 'immer'
import { create } from 'zustand'
import { SvgIconType } from '@/components/svg-icon'

export type PanelId = string

/** Viewport position and size of a floating panel */
export type FloatingPlacement = { x: number, y: number, width: number, height: number }

export type PanelRole = "primary" | "auxiliary" | "floating-only"

type PanelDef = {
  /** Unique identification for this panel */
  id: string

  /** Heading title for this panel */
  title: string

  /**
   * Panel role.
   * - There should only be zero or one "primary" panels active
   * - There can be zero or many additional "auxiliary" panels active,
   *   intended to be related to the "primary" panel
   * - There can be zero or many additional "floating-only" panels active
   */
  role: PanelRole

  /** Icon for this panel */
  icon?: SvgIconType

  /** Sorting order in side-rail, for primary panels */
  sortOrder?: number

  /** Positioned on top or bottom in side-rail, for primary panels */
  position?: "top" | "bottom"

  /** Side-rail icons of different groups are visually separated, for primary panels */
  railGroup?: string

  /** Optional close handler for this panel */
  onClose?: () => void

  /** Whether to automatically make this panel visible when it's registered */
  defaultVisible?: boolean

  /**
   * Whether to automatically float this panel.
   * Panels of role "floating-only" will *always* float regardless of this setting.
   */
  defaultFloating?: boolean

  /** Default dimensions for floating mode */
  defaultFloatingDimensions?: {
    width?: number,
    height?: number
  }

  /**
   * Whether to show close button in the header
   * @defaultValue true
   */
  showCloseButton?: boolean
}

type PanelProps = PanelDef & {
  children: React.ReactNode | ((actions: PanelActions) => React.ReactNode)
  renderHeaderExtraIcons?: (actions: PanelActions) => React.ReactNode
  renderBadge?: React.ReactNode
}

type PanelRegistry = {
  definitions: Record<PanelId, PanelDef>
  renderers: Record<PanelId, (actions: PanelActions) => React.ReactNode>
  headerExtraIconRenderers: Record<PanelId, (actions: PanelActions) => React.ReactNode>
  badgeRenderers: Record<PanelId, () => React.ReactNode>
  closeHandlers: Record<PanelId, () => void>
  openPanels: Set<PanelId>
  /** Also defining the z-index order */
  floatingPanels: PanelId[]
  /** Where a panel floats when it starts floating, e.g. where it was dragged off a widget */
  floatingPlacements: Record<PanelId, FloatingPlacement>

  register: (def: PanelDef) => () => void
  setRenderer: (id: PanelId, render: (actions: PanelActions) => React.ReactNode) => () => void
  setHeaderExtraIconRenderer: (id: PanelId, render: (actions: PanelActions) => React.ReactNode) => () => void
  setBadgeRenderer: (id: PanelId, render: () => React.ReactNode) => () => void
  setCloseHandler: (id: PanelId, onClose: () => void) => () => void
  openPanel: (id: PanelId) => void
  closePanel: (id: PanelId) => void
  floatPanel: (id: PanelId, placement?: FloatingPlacement) => void
  dockPanel: (id: PanelId) => void
  bringToFront: (id: PanelId) => void
}

type PanelActions = {
  float: () => void
  dock: () => void
  close: () => void
}

export const usePanelRegistry = create<PanelRegistry>()((set, get) => ({
  definitions: {},
  renderers: {},
  headerExtraIconRenderers: {},
  badgeRenderers: {},
  closeHandlers: {},
  openPanels: new Set(),
  floatingPanels: [],
  floatingPlacements: {},

  register: (def: PanelDef) => {
    set(produce(st => {
      st.definitions[def.id] = def
      if (def.defaultVisible) {
        st.openPanels.add(def.id)
      }
      if (def.defaultFloating || def.role === "floating-only") {
        if (!st.floatingPanels.includes(def.id)) {
          st.floatingPanels.push(def.id)
        }
      }
    }))
    return () => {
      set(produce(st => {
        delete st.definitions[def.id]
        // Keep the rest of the state, in case panel immediately re-registers
        // (a common valid use-case)
      }))
    }
  },

  setRenderer: (id: PanelId, render: (actions: PanelActions) => React.ReactNode) => {
    set(produce(st => {
      if (!(id in st.definitions)) throw new Error('No definition for panel ID ' + id)
      st.renderers[id] = render
    }))
    return () => {
      set(produce(st => {
        delete st.renderers[id]
      }))
    }
  },

  setHeaderExtraIconRenderer: (id: PanelId, render: (actions: PanelActions) => React.ReactNode) => {
    set(produce(st => {
      if (!(id in st.definitions)) throw new Error('No definition for panel ID ' + id)
      st.headerExtraIconRenderers[id] = render
    }))
    return () => {
      set(produce(st => {
        delete st.headerExtraIconRenderers[id]
      }))
    }
  },

  setBadgeRenderer: (id: PanelId, render: () => React.ReactNode) => {
    set(produce(st => {
      if (!(id in st.definitions)) throw new Error('No definition for panel ID ' + id)
      st.badgeRenderers[id] = render
    }))
    return () => {
      set(produce(st => {
        delete st.badgeRenderers[id]
      }))
    }
  },

  setCloseHandler: (id: PanelId, onClose: () => void) => {
    set(produce(st => {
      if (!(id in st.definitions)) throw new Error('No definition for panel ID ' + id)
      st.closeHandlers[id] = onClose
    }))
    return () => {
      set(produce(st => {
        delete st.closeHandlers[id]
      }))
    }
  },

  openPanel: (id: PanelId) => {
    set(produce(st => {
      if (!(id in st.definitions)) throw new Error('No definition for panel ID ' + id)
      const definition = st.definitions[id]
      if (definition.role === "primary") {
        st.openPanels.clear()
      }
      st.openPanels.add(id)
    }))
  },

  closePanel: (id: PanelId) => {
    set(produce(st => {
      st.openPanels.delete(id)
    }))
  },

  floatPanel: (id: PanelId, placement?: FloatingPlacement) => {
    set(produce(st => {
      if (placement) st.floatingPlacements[id] = placement
      if (st.floatingPanels.includes(id)) return
      st.floatingPanels.push(id)
    }))
  },

  dockPanel: (id: PanelId) => {
    set(produce(st => {
      delete st.floatingPlacements[id]
      const index = st.floatingPanels.indexOf(id)
      st.floatingPanels.splice(index, 1)
    }))
  },

  bringToFront: (id: PanelId) => {
    set(produce(st => {
      const idx = st.floatingPanels.indexOf(id)
      if (idx === -1) return
      if (idx === st.floatingPanels.length - 1) return
      st.floatingPanels.splice(idx, 1)
      st.floatingPanels.push(id)
    }))
  },
}))

export type FloatingState = "docked" | "floating-inactive" | "floating-active"
export const FloatingStateContext = createContext<FloatingState>("docked")
// happy-todo should it not be useIsDocked() ?
export const useFloatingState = () => useContext(FloatingStateContext)

// happy-todo index.ts public apis?

export const useVisiblePanels = () => {
  const definitions = usePanelRegistry(r => r.definitions)
  const openPanels = usePanelRegistry(r => r.openPanels)
  const floatingPanels = usePanelRegistry(r => r.floatingPanels)
  return useMemo(() => {
    return new Set([ ...openPanels ].filter(id => {
      if (!(id in definitions)) return false // valid case
      const def = definitions[id]
      return def.role === "primary" || !floatingPanels.includes(id)
    }))
  }, [ definitions, openPanels, floatingPanels ])
}

export const useHasPrimaryPanelVisible = () => {
  return usePanelRegistry(r => Object
    .keys(r.definitions)
    .some(id => r.definitions[id].role === "primary" && r.openPanels.has(id))
  )
}

export function Panel(props: PanelProps) {
  const render = useMemo(
    () => (actions: PanelActions) => {
      return typeof props.children === "function"
        ? props.children(actions)
        : props.children
    },
    [ props.children ]
  )
  useEffect(() => {
    return usePanelRegistry.getState().register({
      id: props.id,
      title: props.title,
      role: props.role,
      icon: props.icon,
      sortOrder: props.sortOrder,
      position: props.position,
      railGroup: props.railGroup,
      defaultVisible: props.defaultVisible,
      defaultFloatingDimensions: {
        width: props.defaultFloatingDimensions?.width,
        height: props.defaultFloatingDimensions?.height
      },
      defaultFloating: props.defaultFloating,
      showCloseButton: props.showCloseButton
    })
  }, [
    props.id,
    props.title,
    props.role,
    props.icon,
    props.sortOrder,
    props.position,
    props.railGroup,
    props.defaultVisible,
    props.defaultFloatingDimensions?.width,
    props.defaultFloatingDimensions?.height,
    props.defaultFloating,
    props.showCloseButton,
  ])
  useEffect(() => {
    return usePanelRegistry.getState().setRenderer(props.id, render)
  }, [ render ])
  useEffect(() => {
    if (!props.renderHeaderExtraIcons) return
    return usePanelRegistry.getState().setHeaderExtraIconRenderer(props.id, props.renderHeaderExtraIcons)
  }, [ props.renderHeaderExtraIcons ])
  useEffect(() => {
    if (!props.renderBadge) return
    return usePanelRegistry.getState().setBadgeRenderer(props.id, () => props.renderBadge)
  }, [ props.renderBadge ])
  useEffect(() => {
    if (!props.onClose) return
    return usePanelRegistry.getState().setCloseHandler(props.id, props.onClose)
  }, [ props.onClose ])

  return null
}
